import { SentimentPolarity } from '@up2stay/up2promo-api-client';
import type { ChartConfig } from '@up2stay/up2promo-ui';

/**
 * Global chart token semantics (see workspaces/ui/src/styles/tokens.css):
 * chart-1 negative · chart-2 positive · chart-3 neutral · chart-4 stars · chart-5 locations
 */
export const CHART = {
  negative: 'var(--chart-1)',
  positive: 'var(--chart-2)',
  neutral: 'var(--chart-3)',
  star: 'var(--chart-4)',
  location: 'var(--chart-5)',
} as const;

/** @deprecated Use CHART — kept for existing imports */
export const SENTIMENT_CHART_COLORS = {
  positive: CHART.positive,
  neutral: CHART.neutral,
  negative: CHART.negative,
  star: CHART.star,
  location: CHART.location,
  driverPositive: CHART.positive,
  driverNegative: CHART.negative,
} as const;

export function buildSentimentChartConfig(labels: {
  positive: string;
  neutral: string;
  negative: string;
}): ChartConfig {
  return {
    positive: { label: labels.positive, color: CHART.positive },
    neutral: { label: labels.neutral, color: CHART.neutral },
    negative: { label: labels.negative, color: CHART.negative },
  };
}

/**
 * Histogram fill by star count — health signal, not decorative rainbow:
 * 5–4 positive · 3 gold/star · 2–1 negative.
 */
export function ratingBarFill(star: 1 | 2 | 3 | 4 | 5): string {
  const ramps: Record<number, string> = {
    5: CHART.positive,
    4: 'color-mix(in oklab, var(--chart-2) 72%, var(--chart-4))',
    3: CHART.star,
    2: 'color-mix(in oklab, var(--chart-1) 70%, var(--chart-4))',
    1: CHART.negative,
  };
  return ramps[star] ?? CHART.star;
}

/**
 * Filled-star classes by rating — static strings so Tailwind keeps them.
 * ≤2 negative · 3 gold · ≥4 positive.
 */
export function starRatingFilledClass(rating: number): string {
  if (rating <= 2) {
    return 'fill-[var(--chart-1)] text-[var(--chart-1)]';
  }
  if (rating === 3) {
    return 'fill-[var(--chart-4)] text-[var(--chart-4)]';
  }
  return 'fill-[var(--chart-2)] text-[var(--chart-2)]';
}

/** Soft card edge/surface tint by rating — same bands as stars. */
export function reviewRatingSurfaceClass(rating: number): string {
  if (rating <= 2) {
    return 'border-[color-mix(in_oklab,var(--chart-1)_28%,var(--border))] bg-[color-mix(in_oklab,var(--chart-1)_4%,transparent)]';
  }
  if (rating === 3) {
    return 'border-[color-mix(in_oklab,var(--chart-4)_32%,var(--border))] bg-[color-mix(in_oklab,var(--chart-4)_5%,transparent)]';
  }
  return 'border-[color-mix(in_oklab,var(--chart-2)_24%,var(--border))] bg-[color-mix(in_oklab,var(--chart-2)_3%,transparent)]';
}

export const ratingChartConfig = {
  count: { label: 'Reviews', color: CHART.star },
} satisfies ChartConfig;

/** Bar fill by average rating — strong brand for great, rose tint for struggling locations. */
export function locationRatingFill(rating: number): string {
  if (rating >= 4.2) return CHART.location;
  if (rating >= 3.5) {
    return 'color-mix(in oklab, var(--chart-5) 72%, var(--muted-foreground))';
  }
  if (rating >= 2.8) {
    return 'color-mix(in oklab, var(--chart-5) 40%, var(--chart-1))';
  }
  return 'color-mix(in oklab, var(--chart-1) 55%, var(--muted-foreground))';
}

export const locationRankChartConfig = {
  averageRating: { label: 'Rating', color: CHART.location },
} satisfies ChartConfig;

export function buildDriverChartConfig(
  label: string,
  variant: 'positive' | 'negative',
): ChartConfig {
  return {
    count: {
      label,
      color: variant === 'positive' ? CHART.positive : CHART.negative,
    },
  };
}

export { SentimentPolarity };

export function sentimentColor(polarity: SentimentPolarity): string {
  return CHART[polarity];
}

/** Static strings so Tailwind includes them in the CSS bundle. */
const SENTIMENT_BADGE_CLASS: Record<SentimentPolarity, string> = {
  [SentimentPolarity.POSITIVE]:
    'border-transparent bg-[color-mix(in_oklab,var(--chart-2)_16%,transparent)] text-[color-mix(in_oklab,var(--chart-2)_78%,var(--foreground))] ring-1 ring-[color-mix(in_oklab,var(--chart-2)_22%,transparent)]',
  [SentimentPolarity.NEGATIVE]:
    'border-transparent bg-[color-mix(in_oklab,var(--chart-1)_16%,transparent)] text-[color-mix(in_oklab,var(--chart-1)_78%,var(--foreground))] ring-1 ring-[color-mix(in_oklab,var(--chart-1)_22%,transparent)]',
  [SentimentPolarity.NEUTRAL]:
    'border-transparent bg-[color-mix(in_oklab,var(--chart-3)_16%,transparent)] text-[color-mix(in_oklab,var(--chart-3)_78%,var(--foreground))] ring-1 ring-[color-mix(in_oklab,var(--chart-3)_22%,transparent)]',
};

export function sentimentBadgeClass(polarity: SentimentPolarity): string {
  return SENTIMENT_BADGE_CLASS[polarity];
}
